/** A document's lifecycle state as a pill - the word, a glyph and a tone, never colour alone (F-040).
 *
 * Moved out of the old Evidence screen (ADR-0212): the review screen shows the same state, and two
 * definitions of "verified" is how one of them stops matching. The wrapper carries
 * `data-document-state`, the structural form the screen tests and the browser gate read, so neither
 * depends on the pill's markup.
 */

import { StatusPill } from "../components/StatusPill";
import type { StatusTone } from "../components/StatusPill";
import styles from "./Evidence.module.css";

const LABEL: Record<string, string> = {
  received: "Received",
  validated: "Ready to read",
  quarantined: "Refused",
  extracting: "Reading",
  awaiting_review: "Needs review",
  verified: "Verified",
  rejected: "Rejected",
  superseded: "Replaced",
};

/** How a lifecycle state is toned. **The tone picks the icon as well as the tint** (`StatusPill`), and
 * the word always stays beside it, so a state is never carried by colour alone (F-040). A state
 * that needs a person is amber; one a person has resolved is green or red by what they decided; a
 * state nothing in the product writes any more is neutral. A state with no entry is neutral and
 * shows its own name - never a guessed tone. */
const STATE_TONE: Record<string, StatusTone> = {
  received: "neutral",
  validated: "info",
  extracting: "neutral",
  awaiting_review: "warn",
  verified: "ok",
  quarantined: "crit",
  rejected: "crit",
  superseded: "neutral",
};

/** A document's state as a pill: icon, then the word. Exported because the review screen shows the
 * same state and two definitions of "verified" is how one of them stops matching. The wrapper is
 * what carries `data-document-state` - the structural form the screen tests and the browser gate
 * read, so neither depends on the pill's markup. */
export function StateChip({ state }: { state: string }) {
  return (
    <span className={styles.chip} data-document-state={state}>
      <StatusPill tone={STATE_TONE[state] ?? "neutral"}>{LABEL[state] ?? state}</StatusPill>
    </span>
  );
}
